import z from "zod";

import {
  item,
  withMeaning,
  type AnswerMeaning,
  type Meaning,
} from "../../authoring.js";
import {
  experience,
  need,
  needOrNotApplicable,
  strength,
} from "./responseSets.js";

/**
 * A test instrument shaped like a full CANS, so the machinery around
 * instruments meets something of realistic size and shape: every domain, needs
 * and strengths on scales of their own, and a module rated only where it
 * applies.
 *
 * Asks what r1 asks, and gives every group, item and answer an English
 * meaning, so the screens and rules built on meanings have a full-sized
 * instrument to work on. Every word is invented and illustrative, never the
 * Praed Foundation's: it is test content, not CANS. Most items share their
 * scale's description of each rating; a few carry anchors of their own, as a
 * real instrument's items may.
 */

export const name = ["sample_cans", "comprehensive", "1.0", "r2"] as const;

export const groups = {
  life_functioning: "life_functioning",
  behavioural_emotional_needs: "behavioural_emotional_needs",
  risk_behaviours: "risk_behaviours",
  cultural_factors: "cultural_factors",
  strengths: "strengths",
  caregiver_resources_and_needs: "caregiver_resources_and_needs",
  adverse_childhood_experiences: "adverse_childhood_experiences",
} as const;

export const items = {
  family_functioning: "family_functioning",
  living_situation: "living_situation",
  social_functioning: "social_functioning",
  developmental_intellectual: "developmental_intellectual",
  decision_making: "decision_making",
  school_behaviour: "school_behaviour",
  school_achievement: "school_achievement",
  school_attendance: "school_attendance",
  medical_physical: "medical_physical",
  sexual_development: "sexual_development",
  sleep: "sleep",

  psychosis: "psychosis",
  impulsivity_hyperactivity: "impulsivity_hyperactivity",
  depression: "depression",
  anxiety: "anxiety",
  oppositional_behaviour: "oppositional_behaviour",
  conduct: "conduct",
  adjustment_to_trauma: "adjustment_to_trauma",
  anger_control: "anger_control",
  substance_use: "substance_use",

  suicide_risk: "suicide_risk",
  non_suicidal_self_injury: "non_suicidal_self_injury",
  other_self_harm: "other_self_harm",
  danger_to_others: "danger_to_others",
  sexual_aggression: "sexual_aggression",
  runaway: "runaway",
  delinquent_behaviour: "delinquent_behaviour",
  intentional_misbehaviour: "intentional_misbehaviour",

  language: "language",
  traditions_and_rituals: "traditions_and_rituals",
  cultural_stress: "cultural_stress",

  family_strengths: "family_strengths",
  interpersonal: "interpersonal",
  educational_setting: "educational_setting",
  talents_and_interests: "talents_and_interests",
  spiritual_religious: "spiritual_religious",
  cultural_identity: "cultural_identity",
  community_life: "community_life",
  natural_supports: "natural_supports",
  resilience: "resilience",

  caregiver_supervision: "caregiver_supervision",
  caregiver_involvement_with_care: "caregiver_involvement_with_care",
  caregiver_knowledge: "caregiver_knowledge",
  caregiver_organisation: "caregiver_organisation",
  caregiver_social_resources: "caregiver_social_resources",
  caregiver_residential_stability: "caregiver_residential_stability",
  caregiver_medical_physical: "caregiver_medical_physical",
  caregiver_mental_health: "caregiver_mental_health",
  caregiver_substance_use: "caregiver_substance_use",
  caregiver_developmental: "caregiver_developmental",
  caregiver_safety: "caregiver_safety",

  sexual_abuse: "sexual_abuse",
  physical_abuse: "physical_abuse",
  emotional_abuse: "emotional_abuse",
  neglect: "neglect",
  medical_trauma: "medical_trauma",
  witness_to_family_violence: "witness_to_family_violence",
  witness_to_community_violence: "witness_to_community_violence",
  natural_or_man_made_disaster: "natural_or_man_made_disaster",
  war_or_terrorism_affected: "war_or_terrorism_affected",
  victim_or_witness_to_criminal_activity:
    "victim_or_witness_to_criminal_activity",
  disrupted_caregiving: "disrupted_caregiving",
  parental_criminal_behaviour: "parental_criminal_behaviour",
} as const;

/** Every key an earlier version asked and this one does not. The first version retires nothing. */
export const retiredItems = [] as const;

/** A title and a description, in English. */
const en = (title: string, description: string): Meaning => ({
  title: { en: title },
  description: { en: description },
});

type Need = z.infer<typeof need>;

/** How much a need calls for, worded once for every item rated on the scale without anchors of its own. */
const needLevels = [
  {
    value: 0,
    ...en("No need seen", "Nothing here calls for attention at present."),
  },
  {
    value: 1,
    ...en(
      "Keep an eye on it",
      "Something here is worth watching, or heading off before it grows.",
    ),
  },
  {
    value: 2,
    ...en(
      "Needs a plan",
      "This is getting in the way of everyday life, and a plan should address it.",
    ),
  },
  {
    value: 3,
    ...en(
      "Needs help now",
      "This is serious or urgent, and needs a response straight away.",
    ),
  },
] as const satisfies readonly AnswerMeaning<Need>[];

/** The ratings of a need, each anchored by item-specific words but titled as the scale titles it. */
const anchored = (
  descriptions: readonly [string, string, string, string],
): readonly AnswerMeaning<Need>[] =>
  needLevels.map((level) => ({
    ...level,
    description: { en: descriptions[level.value] },
  }));

const needOrNotApplicableLevels = [
  ...needLevels,
  {
    value: "not_applicable",
    ...en(
      "Not in education",
      "They are not at school or college, so this does not apply.",
    ),
  },
] as const satisfies readonly AnswerMeaning<
  z.infer<typeof needOrNotApplicable>
>[];

/** How established a strength is, from one to build a plan around to one not yet found. */
const strengthLevels = [
  {
    value: 0,
    ...en(
      "A real asset",
      "A well-established strength that a plan can be built around.",
    ),
  },
  {
    value: 1,
    ...en("Helps along", "A strength that is there and helps, if not always."),
  },
  {
    value: 2,
    ...en(
      "Starting to show",
      "A strength that has been glimpsed and could be built up.",
    ),
  },
  {
    value: 3,
    ...en("Not yet found", "No strength has been found here so far."),
  },
] as const satisfies readonly AnswerMeaning<z.infer<typeof strength>>[];

const experienceLevels = [
  {
    value: 0,
    ...en("No", "Nothing known suggests this has happened."),
  },
  {
    value: 1,
    ...en("Yes", "This has happened, or there is good reason to think so."),
  },
] as const satisfies readonly AnswerMeaning<z.infer<typeof experience>>[];

const lifeFunctioning = withMeaning(
  z
    .object({
      ...item(items.family_functioning, need, {
        ...en(
          "Getting on at home",
          "How the young person and the people they live with get along.",
        ),
        answers: anchored([
          "They get on well with the people they live with.",
          "There is some friction at home, mostly worked through.",
          "Arguments or distance at home are harming day-to-day life.",
          "Relationships at home have broken down, or are unsafe.",
        ]),
      })
        .withComment()
        .build(),
      ...item(items.living_situation, need, {
        ...en(
          "Where they live",
          "Whether their current home is settled and suits them.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.social_functioning, need, {
        ...en(
          "Friendships",
          "How well they make and keep friends their own age.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.developmental_intellectual, need, {
        ...en(
          "Learning and development",
          "Whether their thinking and development are where you would expect for their age.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.decision_making, need, {
        ...en(
          "Making choices",
          "How well they weigh up options and think about what comes next.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      // Not applicable where the young person is not in education, which is a
      // different fact from having no need there.
      ...item(items.school_behaviour, needOrNotApplicable, {
        ...en(
          "Behaviour at school",
          "How they conduct themselves in lessons and around school.",
        ),
        answers: needOrNotApplicableLevels,
      })
        .withComment()
        .build(),
      ...item(items.school_achievement, needOrNotApplicable, {
        ...en(
          "Progress at school",
          "How their schoolwork compares with what they could achieve.",
        ),
        answers: needOrNotApplicableLevels,
      })
        .withComment()
        .build(),
      ...item(items.school_attendance, needOrNotApplicable, {
        ...en(
          "Going to school",
          "How often they are at school or college when they should be.",
        ),
        answers: needOrNotApplicableLevels,
      })
        .withComment()
        .build(),
      ...item(items.medical_physical, need, {
        ...en(
          "Physical health",
          "Any illness, condition or injury affecting their daily life.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.sexual_development, need, {
        ...en(
          "Growing up and relationships",
          "How they are managing puberty, identity and close relationships.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.sleep, need, {
        ...en(
          "Sleep",
          "Whether they sleep enough, and well enough, to get through the day.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
    })
    .strict(),
  en(
    "Everyday life",
    "How the young person is getting on with home, school, health and the routines of each day.",
  ),
);

const behaviouralEmotionalNeeds = withMeaning(
  z
    .object({
      ...item(items.psychosis, need, {
        ...en(
          "Unusual experiences",
          "Whether they hear, see or believe things others do not.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.impulsivity_hyperactivity, need, {
        ...en(
          "Restlessness",
          "How hard they find it to sit still, wait, or stop before acting.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.depression, need, {
        ...en(
          "Low mood",
          "Sadness, flatness or loss of interest that lingers.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.anxiety, need, {
        ...en(
          "Worry",
          "How much fear or nervousness gets in the way of their day.",
        ),
        answers: anchored([
          "No more worry than is usual for their age.",
          "Some worry or nervousness, which they mostly manage.",
          "Worry regularly stops them doing everyday things.",
          "Fear or panic so strong it overwhelms them, or puts them at risk.",
        ]),
      })
        .withComment()
        .build(),
      ...item(items.oppositional_behaviour, need, {
        ...en(
          "Pushing back",
          "How often they refuse or argue with the adults responsible for them.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.conduct, need, {
        ...en(
          "Breaking rules",
          "Behaviour that hurts others or breaks rules everyone else keeps.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.adjustment_to_trauma, need, {
        ...en(
          "Living with what happened",
          "How they are coping with the effects of something frightening or harmful.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.anger_control, need, {
        ...en("Temper", "How well they manage anger when it comes."),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.substance_use, need, {
        ...en(
          "Alcohol and drugs",
          "Whether drinking or drug use is causing them problems.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
    })
    .strict(),
  en(
    "Feelings and behaviour",
    "How the young person's moods, worries and actions are affecting them.",
  ),
);

const riskBehaviours = withMeaning(
  z
    .object({
      ...item(items.suicide_risk, need, {
        ...en(
          "Thoughts of ending their life",
          "Any sign they have thought about, planned or tried to end their life.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.non_suicidal_self_injury, need, {
        ...en(
          "Hurting themselves",
          "Deliberate injury, such as cutting, without wanting to die.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.other_self_harm, need, {
        ...en(
          "Putting themselves at risk",
          "Reckless acts that could hurt them, short of deliberate injury.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.danger_to_others, need, {
        ...en(
          "Harm to others",
          "Any threat or act of violence towards other people.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.sexual_aggression, need, {
        ...en(
          "Harmful sexual behaviour",
          "Sexual behaviour forced on, or harmful to, someone else.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.runaway, need, {
        ...en(
          "Going missing",
          "Leaving home or a placement without saying where, or staying away.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.delinquent_behaviour, need, {
        ...en(
          "Trouble with the law",
          "Acts that have brought, or could bring, the police in.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.intentional_misbehaviour, need, {
        ...en(
          "Provoking reactions",
          "Misbehaving on purpose to get a response from others.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
    })
    .strict(),
  en(
    "Risks",
    "Anything the young person does that could put themselves or others in harm's way.",
  ),
);

const culturalFactors = withMeaning(
  z
    .object({
      ...item(items.language, need, {
        ...en(
          "Language",
          "Whether the language they speak at home is a barrier to their care.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.traditions_and_rituals, need, {
        ...en(
          "Traditions",
          "Whether they can keep the customs and observances that matter to them.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.cultural_stress, need, {
        ...en(
          "Pressure from culture",
          "Strain from discrimination, or from living between cultures.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
    })
    .strict(),
  en(
    "Culture",
    "How the young person's language, customs and background shape their care.",
  ),
);

const strengths = withMeaning(
  z
    .object({
      ...item(items.family_strengths, strength, {
        ...en("Family bonds", "The love and support their family gives them."),
        answers: strengthLevels,
      })
        .withComment()
        .build(),
      ...item(items.interpersonal, strength, {
        ...en(
          "Getting on with people",
          "Their warmth and skill in relating to others.",
        ),
        answers: strengthLevels,
      })
        .withComment()
        .build(),
      ...item(items.educational_setting, strength, {
        ...en(
          "School as a support",
          "How far their school or college works with them and for them.",
        ),
        answers: strengthLevels,
      })
        .withComment()
        .build(),
      ...item(items.talents_and_interests, strength, {
        ...en("Talents and interests", "Things they enjoy and are good at."),
        answers: strengthLevels,
      })
        .withComment()
        .build(),
      ...item(items.spiritual_religious, strength, {
        ...en(
          "Faith",
          "Comfort or belonging they draw from belief or a faith community.",
        ),
        answers: strengthLevels,
      })
        .withComment()
        .build(),
      ...item(items.cultural_identity, strength, {
        ...en(
          "Sense of heritage",
          "Pride in, and connection to, their own background.",
        ),
        answers: strengthLevels,
      })
        .withComment()
        .build(),
      ...item(items.community_life, strength, {
        ...en(
          "Belonging locally",
          "Their ties to clubs, groups and neighbours.",
        ),
        answers: strengthLevels,
      })
        .withComment()
        .build(),
      ...item(items.natural_supports, strength, {
        ...en(
          "People to turn to",
          "Adults outside services who are there for them.",
        ),
        answers: strengthLevels,
      })
        .withComment()
        .build(),
      ...item(items.resilience, strength, {
        ...en(
          "Bouncing back",
          "Their ability to recover from setbacks and use what they are good at.",
        ),
        answers: strengthLevels,
      })
        .withComment()
        .build(),
    })
    .strict(),
  en(
    "Strengths",
    "What is going well for the young person, which a plan can build on.",
  ),
);

// Every key names the caregiver: several would otherwise be the same word as a
// young person's item, and a key means one thing across a family.
const caregiverResourcesAndNeeds = withMeaning(
  z
    .object({
      ...item(items.caregiver_supervision, need, {
        ...en(
          "Keeping watch",
          "How well the caregiver sets limits and knows what the young person is doing.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.caregiver_involvement_with_care, need, {
        ...en(
          "Taking part in care",
          "How involved the caregiver is in plans and meetings about the young person.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.caregiver_knowledge, need, {
        ...en(
          "Understanding the young person",
          "How well the caregiver understands the young person's needs.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.caregiver_organisation, need, {
        ...en(
          "Running the household",
          "How well the caregiver keeps on top of appointments, money and routines.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.caregiver_social_resources, need, {
        ...en(
          "The caregiver's own support",
          "Family and friends the caregiver can call on.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.caregiver_residential_stability, need, {
        ...en("Settled home", "How secure the caregiver's housing is."),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.caregiver_medical_physical, need, {
        ...en(
          "The caregiver's health",
          "Any physical health problem limiting the caregiver.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.caregiver_mental_health, need, {
        ...en(
          "The caregiver's wellbeing",
          "Any mental health difficulty affecting how the caregiver parents.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.caregiver_substance_use, need, {
        ...en(
          "The caregiver's drinking or drug use",
          "Whether alcohol or drugs affect the care they give.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.caregiver_developmental, need, {
        ...en(
          "The caregiver's learning needs",
          "Any learning difficulty that affects how the caregiver copes.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
      ...item(items.caregiver_safety, need, {
        ...en(
          "Safety at home",
          "Whether the young person is safe with this caregiver.",
        ),
        answers: needLevels,
      })
        .withComment()
        .build(),
    })
    .strict(),
  en(
    "Caregivers",
    "What the adults caring for the young person can offer, and what they need themselves.",
  ),
);

const adverseChildhoodExperiences = withMeaning(
  z
    .object({
      ...item(items.sexual_abuse, experience, {
        ...en("Sexual abuse", "Whether they have been sexually abused."),
        answers: experienceLevels,
      })
        .withComment()
        .build(),
      ...item(items.physical_abuse, experience, {
        ...en(
          "Physical abuse",
          "Whether someone looking after them has hurt them on purpose.",
        ),
        answers: experienceLevels,
      })
        .withComment()
        .build(),
      ...item(items.emotional_abuse, experience, {
        ...en(
          "Emotional abuse",
          "Whether they have been belittled, threatened or rejected over time.",
        ),
        answers: experienceLevels,
      })
        .withComment()
        .build(),
      ...item(items.neglect, experience, {
        ...en(
          "Neglect",
          "Whether their basic needs for food, safety or care went unmet.",
        ),
        answers: experienceLevels,
      })
        .withComment()
        .build(),
      ...item(items.medical_trauma, experience, {
        ...en(
          "Frightening medical care",
          "Whether an illness, injury or treatment was traumatic for them.",
        ),
        answers: experienceLevels,
      })
        .withComment()
        .build(),
      ...item(items.witness_to_family_violence, experience, {
        ...en(
          "Violence at home",
          "Whether they have seen or heard violence between family members.",
        ),
        answers: experienceLevels,
      })
        .withComment()
        .build(),
      ...item(items.witness_to_community_violence, experience, {
        ...en(
          "Violence nearby",
          "Whether they have seen violence in their neighbourhood or school.",
        ),
        answers: experienceLevels,
      })
        .withComment()
        .build(),
      ...item(items.natural_or_man_made_disaster, experience, {
        ...en(
          "Disaster",
          "Whether they have lived through a fire, flood or similar catastrophe.",
        ),
        answers: experienceLevels,
      })
        .withComment()
        .build(),
      ...item(items.war_or_terrorism_affected, experience, {
        ...en(
          "War or terrorism",
          "Whether war or a terrorist attack has touched their life.",
        ),
        answers: experienceLevels,
      })
        .withComment()
        .build(),
      ...item(items.victim_or_witness_to_criminal_activity, experience, {
        ...en(
          "Crime",
          "Whether they have been the victim of a crime, or seen one.",
        ),
        answers: experienceLevels,
      })
        .withComment()
        .build(),
      ...item(items.disrupted_caregiving, experience, {
        ...en(
          "Separation from caregivers",
          "Whether they have lost, or been moved away from, the people caring for them.",
        ),
        answers: experienceLevels,
      })
        .withComment()
        .build(),
      ...item(items.parental_criminal_behaviour, experience, {
        ...en(
          "A parent in trouble with the law",
          "Whether a parent has been involved in crime or imprisoned.",
        ),
        answers: experienceLevels,
      })
        .withComment()
        .build(),
    })
    .strict(),
  en(
    "Hard experiences",
    "Difficult or harmful things that have happened to the young person.",
  ),
);

export const schema = z
  .object({
    [groups.life_functioning]: lifeFunctioning,
    [groups.behavioural_emotional_needs]: behaviouralEmotionalNeeds,
    [groups.risk_behaviours]: riskBehaviours,
    [groups.cultural_factors]: culturalFactors,
    [groups.strengths]: strengths,
    // A module, rated only where there is a caregiver to rate.
    [groups.caregiver_resources_and_needs]:
      caregiverResourcesAndNeeds.optional(),
    [groups.adverse_childhood_experiences]: adverseChildhoodExperiences,
  })
  .strict();
